import React, { useEffect, useMemo, useState } from "react";
import { Typography, useMediaQuery } from "@material-ui/core";
import SearchInput from "../components/Search";
import RecentlyAddedTradeIn from "../components/RecentlyAddedTradeIn";
import NavigationTabs from "../components/NavigationTabs";
import Shipments from "./Shipments";
import PIIReports from "./PIIReports";
import { useTranslation } from "react-i18next";
import { useUserContext } from "../contexts/userContext";

const HOME_ACTIVE_TAB_STORAGE_KEY = "home-active-tab";

const Home = () => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:769px)");
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") {
      return "trade-in";
    }
    return localStorage.getItem(HOME_ACTIVE_TAB_STORAGE_KEY) || "trade-in";
  });
  const { user, isLoading } = useUserContext();
  const role =
    user?.role != null && String(user.role).trim() !== ""
      ? String(user.role).trim()
      : "Support Agent";

  const tabs = [
    { id: "trade-in", label: t("Trade-In") },
    { id: "shipments", label: t("Shipments") },
    { id: "pii-reports", label: t("piiPage_title") },
  ];
  const availableTabs = useMemo(() => {
    // Don't show tabs until user data is loaded
    if (isLoading || !user) {
      return [];
    }
    
    const roleTabAccess = {
      "Support Agent": new Set(["trade-in"]),
      "Store Employee": new Set(["shipments"]),
      "Store Manager": new Set(["shipments", "pii-reports"]),
      "Super User": new Set(["trade-in", "shipments", "pii-reports"]),
    };
    const allowedTabs = roleTabAccess[role];
    if (!allowedTabs) return tabs;
    return tabs.filter((tab) => allowedTabs.has(tab.id));
  }, [role, tabs, isLoading, user]);

  const handleTabChange = (tabId) => {
    console.log("Home handleTabChange called with:", tabId);
    setActiveTab(tabId);
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.setItem(HOME_ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    // Only update activeTab when user data is loaded and tabs are available
    if (isLoading || !user || availableTabs.length === 0) {
      return;
    }
    
    if (!availableTabs.find((tab) => tab.id === activeTab)) {
      setActiveTab(availableTabs[0]?.id || "trade-in");
    }
  }, [activeTab, availableTabs, isLoading, user]);

  const renderContent = () => {
    // Don't render content until user data is loaded
    if (isLoading || !user) {
      return null;
    }

    // Only render content for tabs the user has access to
    const hasAccessToTab = availableTabs.some((tab) => tab.id === activeTab);
    if (!hasAccessToTab) {
      return null;
    }

    if (activeTab === "shipments") {
      return <Shipments onTabChange={handleTabChange} />;
    }

    if (activeTab === "pii-reports") {
      return <PIIReports onTabChange={handleTabChange} />;
    }

    return (
      <>
        <Typography
          style={{
            color: "#212529",
            fontSize: 24,
            fontFamily: "Inter",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "normal",
            letterSpacing: "-0.24px",
            marginTop: 24,
            marginBottom: 25,
          }}
        >
          {t("Trade-In Information")}
        </Typography>
        <SearchInput />
        <RecentlyAddedTradeIn />
      </>
    );
  };

  const contentTopMargin = availableTabs.length > 1 ? 0 : isDesktop ? 135 : 32;

  // Don't render anything until user data is loaded
  if (isLoading || !user) {
    return null;
  }

  return (
    <div style={{ 
      width: "100%", 
      paddingLeft: isDesktop ? 24 : 16, 
      paddingRight: isDesktop ? 24 : 16,
      maxWidth: isDesktop ? "none" : "100%"
    }}>
      {availableTabs.length > 1 && (
      <div style={{ marginTop: isDesktop ? 135 : 32 }}>
        <NavigationTabs
            tabs={availableTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
      )}
      <div style={{ marginTop: contentTopMargin }}>
      {renderContent()}
      </div>
    </div>
  );
};
export default Home;
